import { useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
  faDownload,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import ActionButton from "../ui/ActionButton";
import { getTimeAgo, formatLocalDate } from "../../utils/dateUtils";
import "../../style/DonorInventory.css";

const TransactionsTable = ({ transactions, darkMode }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "dateDelivered",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Ref for scrolling to top of table section
  const sectionRef = useRef(null);

  // filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    // filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (txn) =>
          txn.material?.toLowerCase().includes(search) ||
          txn.receiver?.toLowerCase().includes(search) ||
          txn.receiverCity?.toLowerCase().includes(search) ||
          txn.id?.toLowerCase().includes(search)
      );
    }

    // sorting logic
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // handle null/undefined
        if (aVal == null) aVal = "";
        if (bVal == null) bVal = "";

        // handle dates
        if (
          sortConfig.key.includes("date") ||
          sortConfig.key.includes("Date")
        ) {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }

        // handle numbers
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        // handle strings
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [transactions, searchTerm, sortConfig]);

  // pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(start, start + itemsPerPage);
  }, [filteredTransactions, currentPage]);

  // Scroll to top of section (with offset for breathing room)
  const scrollToTop = () => {
    if (sectionRef.current) {
      // Find the parent accordion container
      const accordion = sectionRef.current.closest('.MuiAccordion-root') || 
                        sectionRef.current.closest('.donor-inv-accordion') ||
                        sectionRef.current.closest('.inv-accordion');
      
      const target = accordion || sectionRef.current;
      
      // Find the scroll container (main-content-area from Layout)
      const scrollContainer = document.getElementById('main-content-area') || 
                              document.querySelector('.main-content') ||
                              window;
      
      const offset = 24; // breathing room above the box
      
      if (scrollContainer === window) {
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      } else {
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const offsetPosition = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - offset;
        scrollContainer.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }
  };

  // handle page change with scroll
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Small delay to allow state to update before scrolling
    setTimeout(scrollToTop, 50);
  };

  // handle sort
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
    setCurrentPage(1);
  };

  // get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  // export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Material",
      "Receiver",
      "City",
      "Quantity (lbs)",
      "Revenue ($)",
      "Date Delivered",
    ];
    const rows = filteredTransactions.map((txn) => [
      txn.material,
      txn.receiver,
      txn.receiverCity || "",
      txn.quantity.toFixed(2),
      txn.revenue.toFixed(2),
      txn.dateDelivered ? formatLocalDate(txn.dateDelivered) : "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `delivery-history-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // column definitions
  const columns = [
    { key: "material", label: t("Material"), width: "auto", sortable: true },
    { key: "receiver", label: t("Receiver"), width: "auto", sortable: true },
    {
      key: "quantity",
      label: t("Quantity"),
      width: "100px",
      sortable: true,
      align: "right",
    },
    {
      key: "revenue",
      label: t("Revenue"),
      width: "100px",
      sortable: true,
      align: "right",
    },
    {
      key: "dateDelivered",
      label: t("Delivered"),
      width: "140px",
      sortable: true,
    },
  ];

  return (
    <div className="transactions-table-section" ref={sectionRef}>
      {/* Header with search and export */}
      <div className="transactions-header">
        <div className="transactions-title-row">
          <div className="transactions-title">
            <FontAwesomeIcon icon={faHistory} className="title-icon" />
            <h5>{t("Delivery History")}</h5>
            <span className="transactions-count">
              {filteredTransactions.length} {t("records")}
            </span>
          </div>
          <ActionButton
            variant="secondary"
            icon={faDownload}
            onClick={handleExportCSV}
            disabled={filteredTransactions.length === 0}
          >
            {t("Export CSV")}
          </ActionButton>
        </div>

        {/* Search */}
        <div className="transactions-search">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder={t("Search by material, receiver, or city...")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="transactions-table-wrapper">
        <table className="transactions-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width, textAlign: col.align || "left" }}
                  className={col.sortable ? "sortable" : ""}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable && (
                    <FontAwesomeIcon
                      icon={getSortIcon(col.key)}
                      className="sort-icon"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-row">
                  {searchTerm
                    ? t("No transactions match your search")
                    : t("No transactions found")}
                </td>
              </tr>
            ) : (
              paginatedTransactions.map((txn) => {
                const deliveredTimeAgo = getTimeAgo(txn.dateDelivered);

                return (
                  <tr key={txn.id}>
                    <td className="material-cell">{txn.material}</td>
                    <td className="receiver-cell">
                      <div className="receiver-info">
                        <span className="receiver-name">{txn.receiver}</span>
                        {txn.receiverCity && (
                          <span className="receiver-city">
                            {txn.receiverCity}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="number-cell">
                      {txn.quantity.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}{" "}
                      lbs
                    </td>
                    <td className="number-cell">
                      $
                      {txn.revenue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    {/* Date Delivered column - at the end */}
                    <td className="date-cell">
                      <div className="date-display">
                        <span className="date-with-icon">
                          <FontAwesomeIcon
                            icon={faTruck}
                            className="delivered-icon"
                          />
                          <span className="time-ago">{deliveredTimeAgo}</span>
                        </span>
                        <span className="full-date">
                          {formatLocalDate(txn.dateDelivered)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="transactions-pagination">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          >
            {t("Previous")}
          </button>
          <span className="pagination-info">
            {t("Page")} {currentPage} {t("of")} {totalPages}
          </span>
          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          >
            {t("Next")}
          </button>
        </div>
      )}
    </div>
  );
};

TransactionsTable.propTypes = {
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      material: PropTypes.string.isRequired,
      receiver: PropTypes.string.isRequired,
      receiverCity: PropTypes.string,
      quantity: PropTypes.number.isRequired,
      revenue: PropTypes.number.isRequired,
      dateAllocated: PropTypes.string,
      dateDelivered: PropTypes.string,
      status: PropTypes.string,
    })
  ).isRequired,
  darkMode: PropTypes.bool,
};

export default TransactionsTable;
